import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  confirmSignIn,
  confirmSignUp,
  fetchAuthSession,
  resendSignUpCode,
  signIn,
  signOut,
} from 'aws-amplify/auth';
import { AuthError, RequestStatus, ADMIN_GROUPS } from '@admiin-com/ds-common';
import { PATHS } from '../../navigation/paths';

export interface ConfirmSignUpFormData {
  email: string;
  password: string;
  code: string;
}

export interface ConfirmMfaFormData {
  code: string;
}

export interface SignInFormData {
  email?: string;
  code?: string;
  password?: string;
}

export interface UseSignInReturn {
  authStatus: RequestStatus;
  authError: AuthError;
  isNewPasswordRequired: boolean;
  mfaCodeRequired: boolean;
  mfaType: 'TOTP' | 'SMS' | null;
  codeDelivery: string;
  deliveryDestination: string;
  submittedEmail: string;
  submittedPassword: string;
  userError: any;
  onSignIn: (data: { email: string; password: string }) => Promise<void>;
  onSubmitNewPassword: (data: { newPassword: string }) => Promise<void>;
  onConfirmSignUp: (data: ConfirmSignUpFormData) => Promise<void>;
  onConfirmMfa: (data: ConfirmMfaFormData) => Promise<void>;
  onResendCode: () => Promise<void>;
  handleInvitationSignIn: (username: string, code: string) => Promise<void>;
  setSubmittedEmail: (email: string) => void;
  setSubmittedPassword: (password: string) => void;
}

export const useSignIn = (): UseSignInReturn => {
  const navigate = useNavigate();
  const [authStatus, setAuthStatus] = useState<RequestStatus>('idle');
  const [authError, setAuthError] = useState<AuthError>({});
  const [isNewPasswordRequired, setIsNewPasswordRequired] =
    useState<boolean>(false);
  const [mfaCodeRequired, setMfaCodeRequired] = useState(false);
  const [mfaType, setMfaType] = useState<'TOTP' | 'SMS' | null>(null);
  const [codeDelivery, setCodeDelivery] = useState<string>('');
  const [deliveryDestination, setDeliveryDestination] = useState<string>('');
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [submittedPassword, setSubmittedPassword] = useState('');

  const checkAuthorised = useCallback(async () => {
    const session = await fetchAuthSession();
    const groups = (session.tokens?.accessToken?.payload?.['cognito:groups'] ||
      []) as string[];
    const authorised = groups.some((g) => ADMIN_GROUPS.includes(g));
    if (!authorised) {
      await signOut();
      setAuthError({ name: 'notAuthorized' });
      setAuthStatus('error');
      return false;
    }
    return true;
  }, []);

  const onSignIn = useCallback(
    async ({ email, password }: { email: string; password: string }) => {
      setAuthStatus('submitting');
      setAuthError({});
      const params = { username: email.toLowerCase().trim(), password };
      try {
        let res;
        try {
          res = await signIn(params);
        } catch (err: any) {
          if (err.name === 'UserAlreadyAuthenticatedException') {
            await signOut();
            res = await signIn(params);
          } else {
            throw err;
          }
        }
        if (
          res.nextStep?.signInStep ===
          'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'
        ) {
          setSubmittedEmail(email);
          setSubmittedPassword(password);
          setIsNewPasswordRequired(true);
        } else if (
          res.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE' ||
          res.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE'
        ) {
          setMfaType(
            res.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE'
              ? 'SMS'
              : 'TOTP'
          );
          setMfaCodeRequired(true);
          setSubmittedEmail(email);
        } else if (res.nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
          setSubmittedEmail(email);
          const { deliveryMedium, destination } = await resendSignUpCode({
            username: email.toLowerCase().trim(),
          });
          setCodeDelivery(deliveryMedium ?? '');
          setDeliveryDestination(destination ?? '');
        } else if (res.isSignedIn) {
          const authorised = await checkAuthorised();
          if (authorised) {
            navigate(PATHS.entities, { replace: true });
          }
        }
        setAuthStatus('success');
      } catch (err: any) {
        if (err.name === 'UserNotConfirmedException') {
          setSubmittedEmail(email);
          try {
            const { deliveryMedium, destination } = await resendSignUpCode({
              username: email.toLowerCase().trim(),
            });
            setCodeDelivery(deliveryMedium ?? '');
            setDeliveryDestination(destination ?? '');
          } catch (e: any) {
            setAuthError(e);
            setAuthStatus('error');
          }
        } else {
          setAuthError(err);
          setAuthStatus('error');
        }
      }
    },
    [checkAuthorised, navigate]
  );

  const onSubmitNewPassword = useCallback(
    async ({ newPassword }: { newPassword: string }) => {
      setAuthStatus('submitting');
      setAuthError({});
      try {
        await confirmSignIn({ challengeResponse: newPassword });
        const authorised = await checkAuthorised();
        if (authorised) {
          navigate(PATHS.entities, { replace: true });
        }
        setAuthStatus('success');
      } catch (err: any) {
        setAuthError(err);
        setAuthStatus('error');
      }
    },
    [checkAuthorised, navigate]
  );

  const onConfirmSignUp = useCallback(
    async ({ email, password, code }: ConfirmSignUpFormData) => {
      setAuthStatus('submitting');
      setAuthError({});
      try {
        await confirmSignUp({ username: email, confirmationCode: code });
        await onSignIn({ email, password });
      } catch (err: any) {
        setAuthError(err);
        setAuthStatus('error');
      }
    },
    [onSignIn]
  );

  const onConfirmMfa = useCallback(
    async ({ code }: ConfirmMfaFormData) => {
      setAuthStatus('submitting');
      setAuthError({});
      try {
        await confirmSignIn({ challengeResponse: code });
        const authorised = await checkAuthorised();
        if (authorised) {
          navigate(PATHS.entities, { replace: true });
        }
        setAuthStatus('success');
      } catch (err: any) {
        setAuthError(err);
        setAuthStatus('error');
      }
    },
    [checkAuthorised, navigate]
  );

  const onResendCode = useCallback(async () => {
    try {
      const { deliveryMedium, destination } = await resendSignUpCode({
        username: submittedEmail,
      });
      setCodeDelivery(deliveryMedium ?? '');
      setDeliveryDestination(destination ?? '');
    } catch (err: any) {
      setAuthError(err);
      setAuthStatus('error');
    }
  }, [submittedEmail]);

  const handleInvitationSignIn = useCallback(
    async (username: string, code: string) => {
      await onSignIn({ email: username, password: code });
    },
    [onSignIn]
  );

  return {
    authStatus,
    authError,
    isNewPasswordRequired,
    mfaCodeRequired,
    mfaType,
    codeDelivery,
    deliveryDestination,
    submittedEmail,
    submittedPassword,
    userError: null,
    onSignIn,
    onSubmitNewPassword,
    onConfirmSignUp,
    onConfirmMfa,
    onResendCode,
    handleInvitationSignIn,
    setSubmittedEmail,
    setSubmittedPassword,
  };
};

export default useSignIn;
